/* eslint-disable import/prefer-default-export */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Row, Col, Select, Space, Typography,
} from 'antd';
import HoverHelp from '../../../../common/HoverHelp';
import TableDatePreload from './TableDatePreload';
import TableProjectPreload from './TableProjectPreload';
import TablePreloadSettings from './TablePreloadSettings';
import TableTimePreload from './TableTimePreload';
import TableScheduleOfValuesPreload from './TableScheduleOfValuesPreload';
import OnTraccrCheckbox from '../../../../common/inputs/OnTraccrCheckbox';

const validDataTypes = new Set([
  'TimeEntry',
  'Equipment',
  'Materials',
  'ScheduleOfValues',
]);

const TIME_TRACKING_GRID_PRELOAD_SOURCES = [
  { value: 'form', label: 'Preload from submitted forms' },
  { value: 'timeEntries', label: 'Preload from existing time entries' },
];

const getDataTypeLabel = (dataType) => {
  switch (dataType) {
    case 'TimeEntry':
      return 'Time Entries';
    case 'Equipment':
      return 'Equipment';
    case 'Materials':
      return 'Materials';
    case 'ScheduleOfValues':
      return 'Schedule of Values';
    default:
      return 'Entries';
  }
};

export function TablePreload({
  configProps,
  updateConfig,
  sections,
  divisions,
  costcodes,
  costcodesLoading,
  templateId,
  isTimeTrackingGrid,
}) {
  const {
    dataType,
    preloadExistingEntries = false,
    timeTrackingGridPreloadSource = 'form',
  } = configProps || {};

  if (!validDataTypes.has(dataType) && !isTimeTrackingGrid) return null;

  const isTimeEntry = dataType === 'TimeEntry';
  const isScheduleOfValues = dataType === 'ScheduleOfValues';
  const showCheckbox = !isTimeEntry;
  const dataTypeLabel = getDataTypeLabel(dataType);

  return (
    <Space direction="vertical" style={{ marginTop: 15 }}>
      {showCheckbox && (
        <OnTraccrCheckbox
          value={preloadExistingEntries}
          onChange={(e) => {
            const payload = { preloadExistingEntries: e };
            if (!e && isTimeTrackingGrid) {
              payload.timeTrackingGridPreloadSource = null;
            }
            updateConfig(payload);
          }}
          label={`Enable Preloading Existing ${dataTypeLabel}`}
        />
      )}
      {(!showCheckbox || preloadExistingEntries) && (
        <>
          {isTimeTrackingGrid && (
            <Space direction="vertical">
              <Row align="middle" gutter={10}>
                <Col>
                <Typography.Text strong>Preload Source</Typography.Text>
                </Col>
                <Col>
                <HoverHelp
                  content={(
                    <div style={{ width: 300 }}>
                      Choose which data source to preload the grid from.
                      <br /><br />
                      Only grids preloaded from form data can create time entries.
                    </div>
                  )}
                />
                </Col>
              </Row>
              <Row align="middle" gutter={10}>
                <Col>
                  <Select
                    options={TIME_TRACKING_GRID_PRELOAD_SOURCES}
                    style={{ width: 420 }}
                    value={timeTrackingGridPreloadSource}
                    onChange={(selectedPreloadSource) => {
                      updateConfig({ timeTrackingGridPreloadSource: selectedPreloadSource });
                    }}
                  />
                </Col>
              </Row>
            </Space>
          )}
          <Typography.Text strong style={{ fontSize: 16 }}>PRELOAD SETTINGS</Typography.Text>
          {isTimeEntry && (
            <>
              <Typography.Text strong>Preload Date/Time for New Entries</Typography.Text>
              <TableDatePreload
                configProps={configProps}
                updateConfig={updateConfig}
                sections={sections}
              />
              <TableTimePreload
                configProps={configProps}
                updateConfig={updateConfig}
                sections={sections}
              />
              <TableProjectPreload
                configProps={configProps}
                updateConfig={updateConfig}
                divisions={divisions}
                sections={sections}
              />
            </>
          )}
          {isTimeTrackingGrid &&
            timeTrackingGridPreloadSource === 'timeEntries' && (
            <TableProjectPreload
              configProps={configProps}
              updateConfig={updateConfig}
              divisions={divisions}
              sections={sections}
            />
          )}
          {isScheduleOfValues ? (
            <TableScheduleOfValuesPreload
              configProps={configProps}
              updateConfig={updateConfig}
              divisions={divisions}
              sections={sections}
            />
          ) : (
            <TablePreloadSettings
              configProps={configProps}
              updateConfig={updateConfig}
              sections={sections}
              divisions={divisions}
              costcodes={costcodes}
              costcodesLoading={costcodesLoading}
              dataType={dataType}
              templateId={templateId}
              isTimeTrackingGrid={isTimeTrackingGrid}
            />
          )}
        </>
      )}
    </Space>
  );
}

TablePreload.propTypes = {
  configProps: PropTypes.shape({
    preloadEnabled: PropTypes.bool,
    preloadExistingEntries: PropTypes.bool,
    preloadDateField: PropTypes.string,
    preloadTimeEntriesFixedDate: PropTypes.string,
    timeTrackingGridPreloadSource: PropTypes.string,
  }).isRequired,
  updateConfig: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodesLoading: PropTypes.bool,
  templateId: PropTypes.string,
  isTimeTrackingGrid: PropTypes.bool,
};

TablePreload.defaultProps = {
  sections: [],
  costcodes: [],
  costcodesLoading: false,
  templateId: null,
  isTimeTrackingGrid: false,
};
